// file: src/components/taskbar/TaskBar.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import styles from './TaskBar.module.css';
import FoundryLogo from '../assets/Foundry_logo_dark_transparent_background.png';
import FoundryLogoLight from '../assets/Foundry_logo_light_transparent_background.png';

/**
 * TaskBar component - Reusable top navigation bar with responsive mobile menu
 * Used across home, pricing, and login pages
 */
export default function TaskBar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        <div className={styles.logo}>
          <Link to="/" className={styles.logoLink}>
            <img 
              src={theme === 'dark' ? FoundryLogo : FoundryLogoLight} 
              alt="FOUNDRY" 
              className={styles.logoImage} 
            />
          </Link>
        </div>
        
        {/* Desktop Navigation */}
        <nav className={styles.nav}>
          <Link to="/pricing" className={styles.navLink}>Pricing</Link>
          <a href="https://www.siameseai.com/" target="_blank" rel="noopener noreferrer" className={styles.navLink}>
            Company <ArrowUpRight size={14} />
          </a>
          <a href="https://www.siameseai.com/contact" target="_blank" rel="noopener noreferrer" className={styles.navLink}>
            Contact <ArrowUpRight size={14} />
          </a>
          
          {/* Theme Toggle Button */}
          <button 
            onClick={toggleTheme}
            className={styles.themeToggle}
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          
          <Link to="/login" className={styles.loginButton}>Log In</Link>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className={styles.mobileMenuButton}
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className={styles.mobileMenu}>
          <Link to="/pricing" className={styles.mobileNavLink} onClick={toggleMobileMenu}>
            Pricing
          </Link>
          <a href="https://www.siameseai.com/" target="_blank" rel="noopener noreferrer" className={styles.mobileNavLink} onClick={toggleMobileMenu}>
            Company <ArrowUpRight size={14} />
          </a>
          <a href="https://www.siameseai.com/contact" target="_blank" rel="noopener noreferrer" className={styles.mobileNavLink} onClick={toggleMobileMenu}>
            Contact <ArrowUpRight size={14} />
          </a>
          
          {/* Theme Toggle for Mobile */}
          <button 
            onClick={() => {
              toggleTheme();
              toggleMobileMenu();
            }}
            className={styles.mobileThemeToggle}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <>
                <Sun size={18} />
                <span>Light Mode</span>
              </>
            ) : (
              <>
                <Moon size={18} />
                <span>Dark Mode</span>
              </>
            )}
          </button>
          
          <Link to="/login" className={styles.mobileLoginButton} onClick={toggleMobileMenu}>
            Log In
          </Link>
        </div>
      )}
    </header>
  );
}
